/**
 * Entrance drawer — the per-section `revealPreset` override (EA-4).
 *
 * Three values, and the difference between two of them is the whole design:
 *
 *   ''      inherit the site preset (no override — the row's dot stays dark)
 *   'none'  this section deliberately does NOT animate (a REAL override)
 *   <id>    this section uses a specific preset
 *
 * "Inherit" and "None" are not the same answer, so they are not the same
 * control state. A user who turns one section off has made a decision the site
 * default must not silently undo later.
 *
 * Every commit flows through `applySectionOverride` / `applySectionOverridesBulk`,
 * so the five-emission contract and the entrance render request (D9) fire
 * unchanged — this module writes nothing itself.
 */

import {
  applySectionOverride,
  applySectionOverridesBulk,
  summarizeOverrideAcross
} from './apply-section-override.js';
import { eventBus } from '../../core/events.js';
import { store } from '../../core/state.js';
import { renderSegmented } from '../design-widgets.js';
import { createButton } from '../components.js';
import { bridge } from '../../editing/bridge.js';
import {
  ENTRANCE_PRESETS,
  ENTRANCE_NONE,
  ENTRANCE_OVERRIDE_FIELD,
  ENTRANCE_DEFAULTS,
  sectionElementEntranceMap
} from '../../shared/entrance-presets.js';
import { sectionTemplateRevealPatterns } from '../../export/scroll-animation-shim.js';

/** Sentinel for the inherit option — `''` is what the store actually holds. */
const INHERIT = '__inherit__';

/** The picker's options, derived FROM the records so they cannot drift. */
export function entranceOptions(sitePresetLabel) {
  return [
    { label: sitePresetLabel ? `Site (${sitePresetLabel})` : 'Site default', value: INHERIT },
    { label: 'None', value: ENTRANCE_NONE },
    ...ENTRANCE_PRESETS.map((preset) => ({ label: preset.label, value: preset.id }))
  ];
}

function sitePresetLabel() {
  const preset = ENTRANCE_PRESETS.find((p) => p.id === store.getState()?.entrance?.preset);
  return preset ? preset.label : 'None';
}

function readOverride(section) {
  const raw = section?.overrides?.[ENTRANCE_OVERRIDE_FIELD];
  if (raw === ENTRANCE_NONE) return ENTRANCE_NONE;
  if (ENTRANCE_PRESETS.some((p) => p.id === raw)) return raw;
  return INHERIT;
}

function sectionsFor(ctx) {
  const page = store.getActivePage();
  const all = Array.isArray(page?.sections) ? page.sections : [];
  const ids =
    Array.isArray(ctx?.instanceIds) && ctx.instanceIds.length
      ? ctx.instanceIds
      : [ctx?.instanceId].filter(Boolean);
  const byId = new Map(all.map((s) => [s.instanceId, s]));
  return ids.map((id) => byId.get(id)).filter(Boolean);
}

/**
 * @param {HTMLElement} container drawer body
 * @param {{instanceId?: string, instanceIds?: string[]}} ctx
 * @returns {() => void} teardown
 */
export function renderEntranceDrawer(container, ctx = {}) {
  if (!container) return () => {};

  const build = () => {
    container.replaceChildren();
    const sections = sectionsFor(ctx);
    const multi = sections.length > 1;

    // MIXED VALUES: with a multi-selection whose sections disagree, no chip is
    // active. Showing one would claim a value the selection does not have.
    const summary = multi
      ? summarizeOverrideAcross(sections, ENTRANCE_OVERRIDE_FIELD)
      : { state: 'uniform' };
    const current = multi
      ? summary.state === 'mixed'
        ? ''
        : readOverride(sections[0])
      : readOverride(sections[0]);

    const row = document.createElement('div');
    row.className = 'pb-override-row pb-entrance-section-row';
    const label = document.createElement('label');
    label.className = 'pb-props-label';
    label.textContent = multi ? `Entrance (${sections.length} sections)` : 'Entrance';
    row.appendChild(label);

    const segment = renderSegmented({
      field: ENTRANCE_OVERRIDE_FIELD,
      ariaLabel: 'Section entrance animation',
      options: entranceOptions(sitePresetLabel()),
      value: current,
      onChange: (nextValue) => {
        // INHERIT is a sentinel the store never sees: `''` is what clears an
        // override, and `renderSegmented` also produces `''` when an active
        // chip is clicked a second time. Both mean the same thing here.
        const stored = !nextValue || nextValue === INHERIT ? '' : nextValue;
        if (multi) {
          applySectionOverridesBulk(
            sections.map((s) => s.instanceId),
            { [ENTRANCE_OVERRIDE_FIELD]: stored },
            { source: 'entrance-drawer', lead: ctx.instanceId || null }
          );
        } else {
          applySectionOverride(sections[0].instanceId, ENTRANCE_OVERRIDE_FIELD, stored, {
            source: 'entrance-drawer'
          });
        }
      }
    });
    row.appendChild(segment);
    container.appendChild(row);

    // EA-10: one-shot replay scoped to THIS section (the lead one on multi).
    container.appendChild(
      createButton({
        label: 'Preview entrance',
        variant: 'ghost',
        onClick: () => bridge.previewEntrance({ instanceId: sections[0].instanceId })
      })
    );

    // ONE ENGINE PER SECTION — say so where the user is choosing, not only in
    // the site drawer they may never open. Nothing is disabled: the preset is
    // stored and takes effect the moment the template convention is switched
    // off, which is what Swap does.
    // EA-9: element mode supersedes THIS control — disclosed, never disabled
    // (clearing the element rows brings the section control back to life).
    const elementModeSections = sections.filter((s) => sectionElementEntranceMap(s));
    if (elementModeSections.length > 0) {
      const note = document.createElement('p');
      note.className = 'pb-feature-desc';
      note.textContent =
        elementModeSections.length === sections.length
          ? sections.length === 1
            ? 'Element animations replace this section entrance. Clear them in the element panels to use a section preset.'
            : 'Element animations replace the section entrance on all of these sections.'
          : `Element animations replace the section entrance on ${elementModeSections.length} of these sections.`;
      container.appendChild(note);
    }

    const templateOwned = sections.filter((s) => sectionTemplateRevealPatterns(s).length > 0);
    if (templateOwned.length > 0 && store.getState()?.entrance?.template !== false) {
      const note = document.createElement('p');
      note.className = 'pb-feature-desc';
      note.textContent =
        templateOwned.length === sections.length
          ? 'This section already animates itself, so it keeps its own animation. Turn the ' +
            "template's scroll reveal off in Buttons & Effects to use a preset here."
          : `${templateOwned.length} of these sections already animate themselves and keep ` +
            'their own animation.';
      container.appendChild(note);
    }

    const scopeNote = document.createElement('p');
    scopeNote.className = 'pb-feature-desc';
    scopeNote.textContent = 'Plays in Preview and on your exported site.';
    container.appendChild(scopeNote);
  };

  build();
  const off = eventBus.on('state-changed', build);
  return () => off?.();
}

/** Inspector-row summary. `—` for inherit, so the row reads as "not set". */
export function renderEntranceSummary(overrides, siteLabel = sitePresetLabel()) {
  // EA-9: element mode SUPERSEDES the section preset (contracts §3), so the
  // row must say what actually ships, not what the superseded control holds.
  const elements = sectionElementEntranceMap({ overrides });
  if (elements) {
    const count = Object.keys(elements).length;
    return `Element animations (${count})`;
  }
  const raw = overrides?.[ENTRANCE_OVERRIDE_FIELD];
  if (raw === ENTRANCE_NONE) return 'None';
  const preset = ENTRANCE_PRESETS.find((p) => p.id === raw);
  if (preset) return preset.label;
  return `Site (${siteLabel || ENTRANCE_DEFAULTS.preset})`;
}
